import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner, ProgressBar, Card, Button, Input } from '../components/ui';
import {
  DocumentTextIcon, ArrowTopRightOnSquareIcon, ChartBarIcon,
  ChevronDownIcon, ChevronRightIcon, CalendarIcon, CalendarDaysIcon,
  MagnifyingGlassIcon, LightBulbIcon, BeakerIcon, RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { useClient, useClientPhases, useClientDocuments, useClientSurveys, calcPhaseProgress } from '../hooks/useClients';
import { useWorkshops, useUpdateBilling } from '../hooks/useWorkshops';
import { useAuth } from '../contexts/AuthContext';
import { useDatabases } from '../hooks/useDatabases';

// ── Shared style maps ────────────────────────────────────────────────────────

const PHASE_ICONS = {
  discovery:      MagnifyingGlassIcon,
  strategy:       LightBulbIcon,
  pilot_planning: BeakerIcon,
  execution:      RocketLaunchIcon,
};

const STATUS = {
  not_started: {
    label: 'Not Started',
    badgeCls: 'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
    barCls:   'bg-border-light dark:bg-border',
    iconBg:   'bg-background-gray dark:bg-surface-dark',
    iconColor:'text-text-muted-light dark:text-text-muted',
  },
  in_progress: {
    label: 'In Progress',
    badgeCls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    barCls:   'bg-brand-primary',
    iconBg:   'bg-blue-100 dark:bg-blue-900/30',
    iconColor:'text-blue-600 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    badgeCls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    barCls:   'bg-green-500',
    iconBg:   'bg-green-100 dark:bg-green-900/30',
    iconColor:'text-green-600 dark:text-green-400',
  },
};

const DELIVERABLE_STATUS_CLS = {
  pending:     'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_review:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  delivered:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};
const DELIVERABLE_STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', in_review: 'In Review', delivered: 'Delivered',
};
const DELIVERABLE_DOT = {
  pending:     'bg-border dark:bg-border',
  in_progress: 'bg-brand-primary',
  in_review:   'bg-amber-400',
  delivered:   'bg-green-500',
};

const ALL_TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'engagement', label: 'Our Engagement' },
  { id: 'documents',  label: 'Documents' },
  { id: 'surveys',    label: 'Surveys' },
  { id: 'workshops',  label: 'Workshops' },
  { id: 'billing',    label: 'Company & Billing' },
];

// ── Read-only phase card (matches EngagementPhases PhaseCard, no edit controls) ─

function ReadOnlyPhaseCard({ phase, isLast }) {
  const [open, setOpen] = useState(phase.status === 'in_progress');
  const s = STATUS[phase.status] || STATUS.not_started;
  const PhaseIcon = PHASE_ICONS[phase.phaseId] || RocketLaunchIcon;

  const completedActivities = phase.activities?.filter(a => a.completed).length ?? 0;
  const totalActivities     = phase.activities?.length ?? 0;
  const deliveredCount      = phase.deliverables?.filter(d => d.status === 'delivered').length ?? 0;
  const totalDeliverables   = phase.deliverables?.length ?? 0;

  return (
    <div className={`relative ${!isLast ? 'pb-3' : ''}`}>
      {/* Vertical connecting line */}
      {!isLast && (
        <div className="absolute left-5 top-[52px] bottom-0 w-px bg-border-light dark:bg-border" />
      )}

      {/* Phase header */}
      <div className="relative z-10 flex items-start gap-4 p-5 rounded-xl border border-border-light dark:border-border bg-white dark:bg-surface-dark">
        <button
          className={`w-10 h-10 min-w-[40px] rounded-xl flex items-center justify-center ${s.iconBg} hover:opacity-80 transition`}
          onClick={() => setOpen(o => !o)}
        >
          <PhaseIcon className={`w-5 h-5 ${s.iconColor}`} />
        </button>

        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen(o => !o)}>
          {/* Top meta row */}
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">
                Phase {phase.order}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badgeCls}`}>
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {phase.startDate && (
                <span className="text-xs text-text-muted-light dark:text-text-muted hidden sm:block">
                  {phase.startDate}{phase.endDate ? ` → ${phase.endDate}` : ''}
                </span>
              )}
              {open
                ? <ChevronDownIcon  className="w-4 h-4 text-text-muted-light dark:text-text-muted" />
                : <ChevronRightIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted" />
              }
            </div>
          </div>

          {/* Phase name */}
          <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-2">{phase.name}</h3>

          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-background-gray dark:bg-background-gray rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${s.barCls}`}
                style={{ width: `${phase.progress || 0}%` }}
              />
            </div>
            <span className="text-xs font-medium text-text-muted-light dark:text-text-muted w-8 text-right flex-shrink-0">
              {phase.progress || 0}%
            </span>
          </div>

          {/* Counts */}
          <div className="flex gap-4 mt-2">
            {totalActivities > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {completedActivities}/{totalActivities} activities
              </span>
            )}
            {totalDeliverables > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {deliveredCount}/{totalDeliverables} delivered
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Expanded detail panel */}
      {open && (
        <div className="ml-14 mt-2 rounded-xl border border-border-light dark:border-border overflow-hidden bg-white dark:bg-surface-dark">

          {/* Activities + Deliverables */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border-light dark:divide-border">
            {/* Activities */}
            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${s.barCls}`} />
                Activities
              </h4>
              <ul className="space-y-1.5">
                {phase.activities?.map(a => (
                  <li
                    key={a.id}
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border"
                  >
                    <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      a.completed ? 'bg-green-500 border-green-500' : 'border-border-light dark:border-border'
                    }`}>
                      {a.completed && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs leading-relaxed ${
                      a.completed
                        ? 'line-through text-text-muted-light dark:text-text-muted'
                        : 'text-text-primary-light dark:text-text-primary'
                    }`}>
                      {a.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Deliverables */}
            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                Deliverables
              </h4>
              <ul className="space-y-1.5">
                {phase.deliverables?.map(d => (
                  <li key={d.id} className="px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DELIVERABLE_DOT[d.status] || DELIVERABLE_DOT.pending}`} />
                      <span className="text-xs text-text-primary-light dark:text-text-primary flex-1 leading-relaxed">{d.title}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DELIVERABLE_STATUS_CLS[d.status] || DELIVERABLE_STATUS_CLS.pending}`}>
                      {DELIVERABLE_STATUS_LABELS[d.status] || d.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main portal component ────────────────────────────────────────────────────

export default function ClientPortal() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { currentUser, permissions } = useAuth();

  const { data: client, isLoading: clientLoading } = useClient(id);
  const { data: phases = [], isLoading: phasesLoading } = useClientPhases(id);
  const TABS = useMemo(
    () => (phases.length > 0 ? ALL_TABS : ALL_TABS.filter((t) => t.id !== 'engagement')),
    [phases.length],
  );
  const validTabIds = TABS.map((t) => t.id);
  const activeTab = validTabIds.includes(tab) ? tab : 'overview';
  const { data: allDocuments = [], isLoading: docsLoading } = useClientDocuments(id);
  const { data: allSurveys = [] } = useClientSurveys(id);
  const { data: databases = [] } = useDatabases();
  const dbLookup = Object.fromEntries(databases.map(db => [db.id || db.slug, db.name]));

  const documents = allDocuments.filter((d) => d.category !== 'internal');

  if (clientLoading || phasesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  const currentPhase  = phases.find((p) => p.status === 'in_progress');

  // KPI data for engagement tab
  const allActivities   = phases.flatMap(p => p.activities || []);
  const allDeliverables = phases.flatMap(p => p.deliverables || []);
  const engProgress     = calcPhaseProgress(allActivities, allDeliverables);
  const completedPhases = phases.filter(p => p.status === 'completed').length;

  const firstName = currentUser?.displayName?.split(' ')[0]
    || (currentUser?.email
      ? currentUser.email.split('@')[0].split('.')[0].replace(/^\w/, c => c.toUpperCase())
      : 'Valued Client');

  return (
    <div className="min-h-screen bg-background-light dark:bg-background">
      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
            {client?.name || 'Client Portal'}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted mt-1">
            Welcome back, {firstName}!
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-border-light dark:border-border mb-8">
          <div className="flex overflow-x-auto gap-0">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/portal/${id}/${t.id}`)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === t.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-text-secondary-light dark:text-text-secondary hover:opacity-75'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── OVERVIEW ─── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {phases.length > 0 ? (
              <Card className="p-5 cursor-pointer hover:opacity-75 transition"
                onClick={() => navigate(`/portal/${id}/engagement`)}
              >
                <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-3">Engagement Progress</h3>
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-4xl font-bold text-brand-primary">{engProgress}%</span>
                  <div className="flex-1">
                    <ProgressBar value={engProgress} colorClass="bg-brand-primary" />
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">
                      {completedPhases} of {phases.length} phases complete
                    </p>
                  </div>
                </div>
                {currentPhase && (
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                    Current phase: <span className="font-medium text-text-primary-light dark:text-text-primary">{currentPhase.name}</span>
                  </p>
                )}
              </Card>
            ) : (
              <Card className="p-5">
                <div className="text-center py-8">
                  <RocketLaunchIcon className="w-10 h-10 mx-auto mb-2 text-text-muted-light dark:text-text-muted opacity-30" />
                  <p className="text-text-muted-light dark:text-text-muted">No engagements</p>
                </div>
              </Card>
            )}

            {/* AI Adoption Insights — only shown if user has insightsAccess */}
            {permissions?.insightsAccess && (
              <Card
                className="p-5 cursor-pointer hover:opacity-75 transition"
                onClick={() => navigate(`/insights?view=company&id=${id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <ChartBarIcon className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary-light dark:text-text-primary">AI Adoption Insights</h3>
                      <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">
                        View your organisation's AI readiness data and benchmarks
                      </p>
                    </div>
                  </div>
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
                </div>
              </Card>
            )}

            {/* Book a Workshop CTA */}
            <Card
              className="p-5 cursor-pointer hover:opacity-75 transition"
              onClick={() => navigate('/book')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <CalendarIcon className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary-light dark:text-text-primary">Book a Workshop</h3>
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">
                      Schedule your next AI workshop with the SeyAI team
                    </p>
                  </div>
                </div>
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
              </div>
            </Card>

            {/* Phase summary */}
            {/* <Card className="p-5">
              <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-4">Engagement Phases</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {phases.map((phase) => (
                  <div key={phase.phaseId} className="text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      phase.status === 'completed'  ? 'bg-green-100 dark:bg-green-900/30' :
                      phase.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900/30' :
                      'bg-border-light dark:bg-surface-dark'
                    }`}>
                      {phase.status === 'completed' ? (
                        <CheckCircleSolid className="w-6 h-6 text-green-600 dark:text-green-400" />
                      ) : phase.status === 'in_progress' ? (
                        <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{phase.progress}%</span>
                      ) : (
                        <span className="text-lg font-bold text-text-muted-light dark:text-text-muted">{phase.order}</span>
                      )}
                    </div>
                    <p className="text-xs font-medium text-text-primary-light dark:text-text-primary">{phase.name}</p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted capitalize">{(phase.status || '').replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </Card> */}

            {/* Key contacts */}
            {/* {client?.contacts?.length > 0 && (
              <Card className="p-5">
                <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-3">Your SeyAI Team</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {client.contacts.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-light dark:bg-surface-dark">
                      <div className="w-9 h-9 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-bold text-brand-primary">
                        {c.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">{c.name}</p>
                        <p className="text-xs text-text-muted-light dark:text-text-muted">{c.role}</p>
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="text-xs text-brand-primary hover:opacity-75 transition">{c.email}</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )} */}
          </div>
        )}

        {/* ─── OUR ENGAGEMENT ─── */}
        {activeTab === 'engagement' && (
          <div className="space-y-6">
            {phasesLoading ? <LoadingSpinner /> : phases.length === 0 ? (
              <div className="text-center py-16">
                <RocketLaunchIcon className="w-10 h-10 mx-auto mb-2 text-text-muted-light dark:text-text-muted opacity-30" />
                <p className="text-text-muted-light dark:text-text-muted">No engagements</p>
              </div>
            ) : (
              <>
                {/* KPI summary row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Overall Progress', value: `${engProgress}%` },
                    { label: 'Activities Done',  value: `${allActivities.filter(a => a.completed).length}/${allActivities.length}` },
                    { label: 'Deliverables',     value: `${allDeliverables.filter(d => d.status === 'delivered').length}/${allDeliverables.length}` },
                  ].map(kpi => (
                    <div key={kpi.label} className="p-4 rounded-xl border border-border-light dark:border-border bg-white dark:bg-surface-dark">
                      <p className="text-xl font-bold text-text-primary-light dark:text-text-primary">{kpi.value}</p>
                      <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mt-0.5">{kpi.label}</p>
                    </div>
                  ))}
                </div>

                {/* Phase list */}
                <div>
                  <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-4">
                    {completedPhases}/{phases.length} phases complete
                  </p>
                  <div>
                    {phases.map((phase, idx) => (
                      <ReadOnlyPhaseCard
                        key={phase.phaseId}
                        phase={phase}
                        isLast={idx === phases.length - 1}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Timeline tab disabled */}

        {/* ─── DOCUMENTS ─── */}
        {activeTab === 'documents' && (
          <div>
            {docsLoading ? (
              <LoadingSpinner />
            ) : documents.length === 0 ? (
              <div className="text-center py-16">
                <DocumentTextIcon className="w-10 h-10 mx-auto mb-2 text-text-muted-light dark:text-text-muted opacity-30" />
                <p className="text-text-muted-light dark:text-text-muted">No documents have been shared yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 p-4 rounded-xl border border-border-light dark:border-border hover:opacity-75 transition bg-white dark:bg-surface-dark"
                  >
                    <DocumentTextIcon className="w-8 h-8 text-brand-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-primary-light dark:text-text-primary truncate">{doc.name}</p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted capitalize">
                        {doc.category?.replace('_', ' ')}
                      </p>
                    </div>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── SURVEYS ─── */}
        {activeTab === 'surveys' && (
          <PortalSurveysTab surveys={allSurveys} dbLookup={dbLookup} />
        )}

        {/* ─── WORKSHOPS ─── */}
        {activeTab === 'workshops' && (
          <PortalWorkshopsTab companyId={id} />
        )}

        {/* ─── COMPANY & BILLING ─── */}
        {activeTab === 'billing' && (
          <BillingTab client={client} companyId={id} />
        )}
      </div>
    </div>
  );
}

function PortalWorkshopsTab({ companyId }) {
  const { data: allBookings = [], isLoading } = useWorkshops();

  const bookings = useMemo(
    () => allBookings.filter((b) => b.companyId === companyId || b.existingCompanyId === companyId),
    [allBookings, companyId],
  );

  const approved = useMemo(() => bookings.filter((b) => b.status === 'approved'), [bookings]);
  const pending  = useMemo(() => bookings.filter((b) => b.status === 'pending'),  [bookings]);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-text-muted-light dark:text-text-muted">Loading workshops…</div>;
  }

  if (bookings.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted-light dark:text-text-muted">No workshop bookings yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Approved */}
      {approved.length > 0 && (
        <div>
          <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted-light dark:text-text-muted mb-3">
            Confirmed Workshops ({approved.length})
          </h2>
          <div className="space-y-3">
            {approved.map((b) => (
              <div
                key={b.id}
                className="bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl p-5"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary-light dark:text-text-primary">
                      {b.preferredDate} · {b.preferredTime}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">{b.location}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Confirmed
                    </span>
                    {b.calendarEventLink && (
                      <a
                        href={b.calendarEventLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-brand-primary hover:underline transition-colors"
                      >
                        <CalendarDaysIcon className="w-3.5 h-3.5" />
                        View in Calendar
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <div>
          <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted-light dark:text-text-muted mb-3">
            Pending <span className="text-amber-500">({pending.length})</span>
          </h2>
          <div className="space-y-3">
            {pending.map((b) => (
              <div
                key={b.id}
                className="bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl p-5"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary-light dark:text-text-primary">
                      {b.preferredDate} · {b.preferredTime}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">{b.location}</p>
                  </div>
                  <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Pending
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BillingTab({ client, companyId }) {
  const updateBilling = useUpdateBilling();
  const [billing, setBilling] = useState({
    billingName:    client?.billingName    || client?.name || '',
    billingEmail:   client?.billingEmail   || '',
    billingAddress: client?.billingAddress || '',
    vatNumber:      client?.vatNumber      || '',
  });
  const [saved, setSaved] = useState(false);

  const set = (field) => (e) => {
    setBilling((b) => ({ ...b, [field]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async () => {
    await updateBilling.mutateAsync({ id: companyId, ...billing });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Company Info (read-only) */}
      <Card className="p-5">
        <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-4">Company Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          {[
            { label: 'Name',            value: client?.name },
            { label: 'Industry',        value: client?.industry },
            { label: 'Village / Town',  value: client?.village },
            { label: 'Size',            value: client?.organisationSize },
            { label: 'Website',         value: client?.website },
          ].map(({ label, value }) => value ? (
            <div key={label}>
              <p className="text-xs text-text-muted-light dark:text-text-muted">{label}</p>
              <p className="font-medium text-text-primary-light dark:text-text-primary mt-0.5">{value}</p>
            </div>
          ) : null)}
        </div>
      </Card>

      {/* Billing Details (editable) */}
      <Card className="p-5">
        <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-1">Billing Details</h3>
        <p className="text-xs text-text-muted-light dark:text-text-muted mb-4">
          Used for invoicing purposes. No payment is taken through this portal.
        </p>
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Name</label>
            <Input type="text" value={billing.billingName} onChange={set('billingName')} className="w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Email</label>
            <Input type="email" value={billing.billingEmail} onChange={set('billingEmail')} className="w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Address</label>
            <textarea
              value={billing.billingAddress}
              onChange={set('billingAddress')}
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">VAT Number</label>
            <Input type="text" placeholder="MT12345678" value={billing.vatNumber} onChange={set('vatNumber')} className="w-full" />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Button variant="primary" onClick={handleSave} disabled={updateBilling.isPending}>
              {updateBilling.isPending ? 'Saving…' : 'Save Billing Details'}
            </Button>
            {saved && <span className="text-sm text-success">Saved!</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}

function PortalSurveysTab({ surveys, dbLookup = {} }) {
  const [copiedId, setCopiedId] = useState(null);
  const openSurveys = surveys.filter((s) => s.isOpen);

  const baseUrl = (process.env.REACT_APP_BASE_URL || window.location.origin).replace(/\/$/, '');

  const copyLink = async (surveyId) => {
    const url = `${baseUrl}/survey/${surveyId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedId(surveyId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (openSurveys.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted-light dark:text-text-muted">No active surveys at the moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {openSurveys.map((survey) => {
        const url = `${baseUrl}/survey/${survey.id}`;
        return (
          <div
            key={survey.id}
            className="bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl p-5"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-text-primary-light dark:text-text-primary">{survey.title}</h3>
                  {survey.databaseId && dbLookup[survey.databaseId] && (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                      {dbLookup[survey.databaseId]}
                    </span>
                  )}
                </div>
                {survey.description && (
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-1">{survey.description}</p>
                )}
                <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">
                  {survey.responseCount ?? 0} response{survey.responseCount !== 1 ? 's' : ''}
                  {survey.createdAt ? ` · Created ${new Date(survey.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm font-medium text-text-secondary-light dark:text-text-secondary border border-border-light dark:border-border rounded-lg px-3 py-1.5 hover:opacity-75 transition"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  View Survey
                </a>
                <button
                  onClick={() => copyLink(survey.id)}
                  className="flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:opacity-70 transition border border-brand-primary rounded-lg px-3 py-1.5"
                >
                  {copiedId === survey.id ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 bg-background-light dark:bg-background rounded-lg px-3 py-2 border border-border-light dark:border-border">
              <span className="text-xs text-text-muted-light dark:text-text-muted font-mono truncate flex-1">{url}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
